import { useState, Fragment } from 'react';
import { Menu, Transition } from '@headlessui/react';
import {
  Bars3Icon,
  MagnifyingGlassIcon,
  BellIcon,
  QuestionMarkCircleIcon,
  UserCircleIcon,
  Cog6ToothIcon,
  ArrowRightOnRectangleIcon,
} from '@heroicons/react/24/outline';
import { useAuthState } from '../../hooks/useAuth';

interface HeaderProps {
  onMenuClick: () => void;
  sidebarCollapsed: boolean;
}

export function Header({ onMenuClick }: HeaderProps) {
  const { user } = useAuthState();
  const [searchQuery, setSearchQuery] = useState('');

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Search:', searchQuery);
  };

  return (
    <header className="h-14 bg-white/5 backdrop-blur-xl border-b border-white/10 flex items-center justify-between px-4 gap-4">
      {/* Left section: Mobile menu + Search */}
      <div className="flex items-center gap-4 flex-1">
        {/* Mobile menu button */}
        <button
          onClick={onMenuClick}
          className="lg:hidden p-2 rounded-md hover:bg-white/10 text-gray-400"
        >
          <Bars3Icon className="h-5 w-5" />
        </button>

        {/* Search bar */}
        <form onSubmit={handleSearch} className="relative flex-1 max-w-md">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
          <input
            type="text"
            placeholder="Search everything... (Ctrl+K)"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 text-sm bg-white/10 border border-white/10 rounded-lg
                     text-white placeholder-gray-500 focus:bg-white/15 focus:border-primary-500 focus:ring-1 focus:ring-primary-500
                     focus:outline-none transition-colors"
          />
          <kbd className="hidden sm:flex absolute right-3 top-1/2 -translate-y-1/2 items-center gap-1
                        px-2 py-0.5 text-xs text-gray-500 bg-white/10 border border-white/10 rounded">
            <span className="text-xs">&#8984;</span>K
          </kbd>
        </form>
      </div>

      {/* Right section: Actions + User menu */}
      <div className="flex items-center gap-2">
        {/* Help button */}
        <button
          className="p-2 rounded-md hover:bg-white/10 text-gray-400 hover:text-gray-200 hidden sm:block"
          title="Help"
        >
          <QuestionMarkCircleIcon className="h-5 w-5" />
        </button>

        {/* Notifications */}
        <button
          className="relative p-2 rounded-md hover:bg-white/10 text-gray-400 hover:text-gray-200"
          title="Notifications"
        >
          <BellIcon className="h-5 w-5" />
          {/* Notification badge */}
          <span className="absolute top-1.5 right-1.5 h-2 w-2 bg-red-500 rounded-full" />
        </button>

        {/* User menu */}
        <Menu as="div" className="relative">
          <Menu.Button className="flex items-center gap-2 p-1.5 rounded-md hover:bg-white/10">
            <div className="h-8 w-8 rounded-full bg-primary-500/20 flex items-center justify-center">
              <span className="text-sm font-medium text-primary-400">
                {user?.name?.charAt(0) ?? '?'}
              </span>
            </div>
            <span className="hidden sm:block text-sm font-medium text-gray-300">
              {user?.name?.split(' ')[0] ?? 'User'}
            </span>
          </Menu.Button>

          <Transition
            as={Fragment}
            enter="transition ease-out duration-100"
            enterFrom="transform opacity-0 scale-95"
            enterTo="transform opacity-100 scale-100"
            leave="transition ease-in duration-75"
            leaveFrom="transform opacity-100 scale-100"
            leaveTo="transform opacity-0 scale-95"
          >
            <Menu.Items className="absolute right-0 mt-2 w-56 z-[60] dropdown-glass">
              {/* User info */}
              <div className="px-4 py-3 border-b border-white/10">
                <p className="text-sm font-medium text-gray-100">{user?.name ?? 'User'}</p>
                <p className="text-xs text-gray-500 truncate">{user?.email ?? ''}</p>
              </div>

              {/* Menu items */}
              <div className="py-1">
                <Menu.Item>
                  {({ active }) => (
                    <button
                      className={`
                        w-full flex items-center gap-3 px-4 py-2 text-sm
                        ${active ? 'bg-white/10' : ''}
                        text-gray-300
                      `}
                    >
                      <UserCircleIcon className="h-5 w-5 text-gray-500" />
                      My Profile
                    </button>
                  )}
                </Menu.Item>
                <Menu.Item>
                  {({ active }) => (
                    <button
                      className={`
                        w-full flex items-center gap-3 px-4 py-2 text-sm
                        ${active ? 'bg-white/10' : ''}
                        text-gray-300
                      `}
                    >
                      <Cog6ToothIcon className="h-5 w-5 text-gray-500" />
                      Settings
                    </button>
                  )}
                </Menu.Item>
              </div>

              <div className="border-t border-white/10 py-1">
                <Menu.Item>
                  {({ active }) => (
                    <button
                      className={`
                        w-full flex items-center gap-3 px-4 py-2 text-sm
                        ${active ? 'bg-white/10' : ''}
                        text-red-400
                      `}
                    >
                      <ArrowRightOnRectangleIcon className="h-5 w-5" />
                      Sign out
                    </button>
                  )}
                </Menu.Item>
              </div>
            </Menu.Items>
          </Transition>
        </Menu>
      </div>
    </header>
  );
}
